import FetchError from "./errors";
import { IJob, IJobRequest, IJobs, IOrgJobListRequest, IQueueRequest, IUpdateJobRequest, IUpdateJobResultRequest, IUserJobListRequest } from "../models";
import { customFetchWithRetry } from "./shared";
import { isElectron } from "../utils/miscUtils";

export const createJob = async (jobRequest: IJobRequest): Promise<Boolean | null> => {
  try {
    const response = await customFetchWithRetry(`/jobv2`, {
      method: "POST",
      body: JSON.stringify(jobRequest)
    })
    if (response.ok) {
      return response.json();
    }

    throw new FetchError(`Error fetching. ${await response.text()}`, response.status);
  } catch (e) {
    console.log('error', e)
    throw new FetchError(e, 500);
  }
};

// used only for test purpose
export const queueJob = async (request: IQueueRequest): Promise<Boolean | null> => {
  try {
    const response = await customFetchWithRetry(`/job/queue`, {
      method: "POST",
      body: JSON.stringify(request)
    })
    if (response.ok) {
      return response.json();
    }

    throw new FetchError(`Error fetching. ${await response.text()}`, response.status);
  } catch (e) {
    console.log('error', e)
    throw new FetchError(e, 500);
  }
};

export const updateJob = async (jobRequest: IUpdateJobRequest): Promise<Boolean | null> => {
  try {
    const response = await customFetchWithRetry(`/job`, {
      method: "PUT",
      body: JSON.stringify(jobRequest)
    })
    if (response.ok) {
      return response.json();
    }

    throw new FetchError(`Error fetching. ${await response.text()}`, response.status);
  } catch (e) {
    throw new FetchError(e, 500);
  }
};

export const updateJobResult = async (request: IUpdateJobResultRequest): Promise<Boolean | null> => {
  try {
    const response = await customFetchWithRetry(`/jobv2/${request.jobId}/result`, {
      method: "POST",
      body: JSON.stringify(request)
    })
    if (response.ok) {
      return response.json();
    }

    throw new FetchError(`Error fetching. ${await response.text()}`, response.status);
  } catch (e) {
    throw new FetchError(e, 500);
  }
};

export const getJobs = async (request: IUserJobListRequest): Promise<IJobs | null> => {
  try {
    var requestUrl = `/user/${request.username}/jobs`
    if (request.from !== undefined && request.to !== undefined) {
      requestUrl = `${requestUrl}?from=${request.from}&to=${request.to}`
    }
    const response = await customFetchWithRetry(requestUrl)
    if (response.ok) {
      return response.json();
    }

    throw new FetchError(`Error fetching. ${await response.text()}`, response.status);
  } catch (e) {
    throw new FetchError(e, 500);
  }
};

export const getOrgJobs = async (request: IOrgJobListRequest): Promise<IJobs | null> => {
  try {
    var requestUrl = `/org/${request.orgId}/jobs`
    if (request.from !== undefined && request.to !== undefined) {
      requestUrl = `${requestUrl}?from=${request.from}&to=${request.to}`
    }
    const response = await customFetchWithRetry(requestUrl)
    if (response.ok) {
      return response.json();
    }

    throw new FetchError(`Error fetching. ${await response.text()}`, response.status);
  } catch (e) {
    throw new FetchError(e, 500);
  }
};

export const getJob = async (jobId: string): Promise<IJob | null> => {
  try {
    const response = await customFetchWithRetry(`/job/${jobId}`)
    if (response.ok) {
      return response.json();
    }

    throw new FetchError(`Error fetching. ${await response.text()}`, response.status);
  } catch (e) {
    throw new FetchError(e, 500);
  }
};

export const getJobUnified = async (jobId: string): Promise<IJob | null> => {
  if (isElectron() && typeof window !== 'undefined') {
    try {
      const localJob = await (window as any).electron.getLocalJob(jobId);
      if (localJob) {
        return localJob;
      }
    } catch (error) {
      console.log('Failed to fetch local job, falling back to cloud');
    }
  }

  const cloudJob = await getJob(jobId);
  if (cloudJob && !cloudJob.processingMode) {
    cloudJob.processingMode = 'cloud';
  }
  return cloudJob;
};

export const getUserJobsUnified = async (request: IUserJobListRequest): Promise<IJobs | null> => {
  let cloudJobs: IJobs | null = null;

  try {
    cloudJobs = await getJobs(request);
  } catch (error) {
    console.log('Failed to fetch cloud jobs (may be offline)');
  }

  let localJobs: IJob[] = [];
  if (isElectron() && typeof window !== 'undefined') {
    try {
      localJobs = await (window as any).electron.getLocalJobs({
        username: request.username,
        fromTimestamp: request.from,
        toTimestamp: request.to
      });
    } catch (error) {
      console.error('Failed to fetch local jobs:', error);
    }
  }

  const jobMap = new Map<string, IJob>();
  localJobs.forEach((job) => {
    if (job && (job as any).jobId) {
      jobMap.set(job.jobId, job);
    }
  });
  cloudJobs?.Items?.forEach((job) => {
    if (!job || !(job as any).jobId) {
      return;
    }
    const localExisting = jobMap.get(job.jobId);
    if (localExisting?.processingMode === 'local') {
      // Preserve local-only sync metadata for jobs that originated on desktop.
      // The cloud job record is still the source of truth for server-side fields.
      const merged: IJob = {
        ...job,
        processingMode: 'local',
        // Keep local timestamps stable so syncing doesn't reorder the job list.
        createdAt: localExisting.createdAt ?? job.createdAt,
        updatedAt: localExisting.updatedAt ?? job.updatedAt,
        syncStatus: localExisting.syncStatus,
        syncError: localExisting.syncError,
        syncedAt: localExisting.syncedAt
      };
      jobMap.set(job.jobId, merged);
      return;
    }

    if (!job.processingMode) {
      job.processingMode = 'cloud';
    }
    jobMap.set(job.jobId, job);
  });

  const mergedJobs = Array.from(jobMap.values()).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

  return { Items: mergedJobs };
};

export const deleteJob = async (jobId: string): Promise<Boolean | null> => {
  try {
    const response = await customFetchWithRetry(`/job/${jobId}`, {
      method: "DELETE"
    });
    if (response.ok) {
      return response.json();
    }
    throw new FetchError(`Error fetching. ${await response.text()}`, response.status);
  } catch (e) {
    throw new FetchError(e, 500);
  }
};

export const deleteJobUnified = async (jobId: string): Promise<Boolean | null> => {
  if (isElectron() && typeof window !== 'undefined') {
    try {
      const localJob = await (window as any).electron.getLocalJob(jobId);
      if (localJob && localJob.processingMode === 'local' && (window as any).electron?.deleteLocalJob) {
        const result = await (window as any).electron.deleteLocalJob(jobId);
        if (result?.success) {
          return true;
        }
        throw new Error(result?.error || 'Failed to delete local job');
      }
    } catch (error) {
      console.log('Failed to delete local job, falling back to cloud');
    }
  }

  return deleteJob(jobId);
};
